import { useEffect } from "react";
import { SITE } from "@/lib/site";

interface SEOProps {
  title: string;
  description: string;
  path: string; // e.g. "/services"
  image?: string;
  type?: "website" | "article";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
}

/**
 * Dependency-free document head manager.
 *
 * Replaces react-helmet-async, which is pinned to a version that injects nothing
 * in the production build — every route ended up sharing index.html's static
 * title/description with no canonical at all. This writes the head imperatively
 * so each route gets its own metadata.
 */

const upsertMeta = (attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const SEO = ({ title, description, path, image, type = "website", jsonLd, noindex }: SEOProps) => {
  const url = `${SITE.url}${path === "/" ? "" : path}`;
  const ogImage = image || `${SITE.url}/goodchoice-removebg-preview.png`;
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const schemaKey = JSON.stringify(schemas);

  useEffect(() => {
    document.title = title;
    upsertMeta("name", "description", description);
    upsertLink("canonical", url);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:image", ogImage);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);

    const robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (noindex) {
      upsertMeta("name", "robots", "noindex, follow");
    } else if (robots) {
      robots.remove();
    }
  }, [title, description, url, type, ogImage, noindex]);

  useEffect(() => {
    if (!schemas.length) return;
    const nodes = schemas.map((schema) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.dataset.seoJsonld = "true";
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
      return el;
    });
    return () => nodes.forEach((el) => el.remove());
    // schemaKey serialises the schema objects so inline literals don't re-run this every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemaKey]);

  return null;
};

export default SEO;
